import React, { useState } from "react";
import dayjs from "dayjs";
import { CustomTimePicker } from "../../components";

const SampleTimePickerUsage = () => {
  const [time1, setTime1] = useState(null);
  const [time2, setTime2] = useState(dayjs("2024-01-01T09:30"));
  const [time3, setTime3] = useState(null);
  const [time4, setTime4] = useState(null);
  const [time5, setTime5] = useState(null);

  return (
    <div style={{ padding: "2rem", maxWidth: "800px", margin: "0 auto" }}>
      <h1 style={{ marginBottom: "2rem", color: "#1f2937" }}>
        CustomTimePicker Usage Examples
      </h1>

      {/* 12-Hour Format (AM/PM) */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          12-Hour Format (AM/PM)
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Event Time"
            name="ampmTime"
            value={time3}
            onChange={(newValue) => {
              setTime3(newValue);
            }}
            format="hh:mm a"
          />
          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected:{" "}
            {time3
              ? `${time3.format("hh:mm A")} , ${time3.format("hh:mm:ss")}`
              : "None"}
          </p>
        </div>
      </section>

      {/* Disabled */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>Disabled</h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Locked Time"
            name="disabledTime"
            defaultValue={dayjs("2024-01-01T10:00")}
            disabled
          />
        </div>
      </section>

      {/* Different Sizes */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Different Sizes
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Small"
            name="smallTime"
            size="sm"
            defaultValue={dayjs("2024-01-01T08:00")}
          />
          <CustomTimePicker
            label="Medium (Default)"
            name="mediumTime"
            size="md"
            defaultValue={dayjs("2024-01-01T12:00")}
          />
          <CustomTimePicker
            label="Large"
            name="largeTime"
            size="lg"
            defaultValue={dayjs("2024-01-01T18:00")}
          />
        </div>
      </section>

      {/* Min and Max Time */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Min and Max Time
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Working Hours Only"
            name="restrictedTime"
            value={time4}
            onChange={(newValue) => setTime4(newValue)}
            minTime={dayjs().hour(9).minute(0)}
            maxTime={dayjs().hour(17).minute(0)}
            helperText="Available: 9:00 AM - 5:00 PM"
            format="hh:mm a"
          />
          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected: {time4 ? time4.format("hh:mm A") : "None"}
          </p>
        </div>
      </section>

      {/* Basic Usage */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>Basic Usage</h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Select Time"
            name="basicTime"
            value={time1}
            onChange={(newValue) => setTime1(newValue)}
          />
          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected: {time1 ? time1.format("HH:mm") : "None"}
          </p>
        </div>
      </section>

      {/* With Default Value */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          With Default Value
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Meeting Time"
            name="defaultTime"
            defaultValue={dayjs("2024-01-01T15:30")}
          />
        </div>
      </section>

      {/* Controlled Value */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Controlled Value
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Appointment Time"
            name="controlledTime"
            value={time2}
            onChange={(newValue) => setTime2(newValue)}
          />
          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Selected: {time2 ? time2.format("HH:mm") : "None"}
          </p>
        </div>
      </section>

      {/* 24-Hour Format */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          24-Hour Format
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Shift Start"
            name="militaryTime"
            defaultValue={dayjs("2024-01-01T14:00")}
            format="HH:mm"
          />
        </div>
      </section>

      {/* Required Field */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Required Field
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Start Time"
            name="requiredTime"
            required
            placeholder="Required field"
          />
        </div>
      </section>

      {/* With Error */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>With Error</h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Deadline"
            name="errorTime"
            error="Please select a valid time"
            required
          />
        </div>
      </section>

      {/* With Helper Text */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          With Helper Text
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Office Hours"
            name="helperTime"
            helperText="Select time between 9 AM - 5 PM"
            format="hh:mm a"
          />
        </div>
      </section>

      {/* Custom Placeholder */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Custom Placeholder
        </h2>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            maxWidth: "300px",
          }}
        >
          <CustomTimePicker
            label="Reminder"
            name="placeholderTime"
            placeholder="Tap to select time"
          />
        </div>
      </section>

      {/* Practical Examples */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          Practical Examples
        </h2>

        {/* Appointment Form */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1.5rem",
            marginBottom: "1.5rem",
            maxWidth: "400px",
          }}
        >
          <h3 style={{ margin: "0 0 1rem 0", color: "#1f2937" }}>
            <span
              className="material-icons"
              style={{ verticalAlign: "middle", marginRight: "0.5rem" }}
            >
              event
            </span>
            Schedule Appointment
          </h3>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <CustomTimePicker
              label="Start Time"
              name="appointmentStart"
              required
              format="hh:mm a"
            />
            <CustomTimePicker
              label="End Time"
              name="appointmentEnd"
              required
              format="hh:mm a"
            />
          </div>
        </div>

        {/* Alarm Settings */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1.5rem",
            marginBottom: "1.5rem",
            maxWidth: "400px",
            backgroundColor: "#f9fafb",
          }}
        >
          <h3 style={{ margin: "0 0 1rem 0", color: "#1f2937" }}>
            <span
              className="material-icons"
              style={{ verticalAlign: "middle", marginRight: "0.5rem" }}
            >
              alarm
            </span>
            Set Alarm
          </h3>
          <CustomTimePicker
            label="Wake Up Time"
            name="alarmTime"
            value={time5}
            onChange={(newValue) => setTime5(newValue)}
            format="hh:mm a"
            defaultValue={dayjs("2024-01-01T07:00")}
          />
          {time5 && (
            <p
              style={{
                color: "#059669",
                fontSize: "0.875rem",
                marginTop: "0.5rem",
              }}
            >
              <span
                className="material-icons"
                style={{
                  fontSize: "1rem",
                  verticalAlign: "middle",
                  marginRight: "0.25rem",
                }}
              >
                check_circle
              </span>
              Alarm set for {time5.format("hh:mm A")}
            </p>
          )}
        </div>

        {/* Business Hours */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1.5rem",
            maxWidth: "400px",
          }}
        >
          <h3 style={{ margin: "0 0 1rem 0", color: "#1f2937" }}>
            <span
              className="material-icons"
              style={{ verticalAlign: "middle", marginRight: "0.5rem" }}
            >
              schedule
            </span>
            Business Hours
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <CustomTimePicker
              label="Opens At"
              name="openTime"
              defaultValue={dayjs("2024-01-01T09:00")}
              format="hh:mm a"
              size="sm"
            />
            <CustomTimePicker
              label="Closes At"
              name="closeTime"
              defaultValue={dayjs("2024-01-01T18:00")}
              format="hh:mm a"
              size="sm"
            />
          </div>
        </div>
      </section>

      {/* All States Summary */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{ marginBottom: "1rem", color: "#374151" }}>
          All States Summary
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1.5rem",
          }}
        >
          <CustomTimePicker
            label="Normal"
            name="normalState"
            defaultValue={dayjs("2024-01-01T10:00")}
          />
          <CustomTimePicker label="Required" name="requiredState" required />
          <CustomTimePicker
            label="With Error"
            name="errorState"
            error="Invalid time"
            required
          />
          <CustomTimePicker
            label="With Helper"
            name="helperState"
            helperText="Choose wisely"
          />
          <CustomTimePicker
            label="Disabled"
            name="disabledState"
            defaultValue={dayjs("2024-01-01T12:00")}
            disabled
          />
          <CustomTimePicker
            label="12-Hour Format"
            name="ampmState"
            format="hh:mm a"
            defaultValue={dayjs("2024-01-01T14:30")}
          />
        </div>
      </section>
    </div>
  );
};

export default SampleTimePickerUsage;
